import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AddOnTextInput, Alert, Button, Icon, Typography } from "@/components/atoms";
import { extractAirportIata } from "@/helpers/bookingHelpers";
import { flightDisruptionReason, isSameFlightNumber } from "@/helpers/bookingHelpers";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  setPickupDatetime,
  setPickupFlightDelayMinutes,
  setPickupFlightLandingUsed,
  setPickupTimeSource,
} from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectDepartureFlightChangedSinceCommit,
  selectDepartureFlightNumber,
  selectDestination,
  selectEditJobId,
  selectPickup,
  selectPickupDatetime,
  selectPickupFlightDelayMinutes,
  selectPickupFlightLandingUsed,
  selectPickupFlightDerivedArrival,
  selectPickupFlightDerivedArrivalSource,
  selectEditOriginal,
  selectPickupFlightChangedSinceCommit,
  selectPickupFlightFrozenReason,
  selectPickupFlightNumber,
  selectPickupNotFlying,
  selectPickupTimeSource,
} from "@/redux/slices/clientBooking/selectors";
import { waitingTimesSelector } from "@/redux/slices/operator/selectors";
import { clsx } from "@/utils";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import "./DateTimePanel.css";
import { FlightDetailsCard } from "./FlightDetailsCard";
import { FlightDetailsModal } from "./FlightDetailsModal";
import { FlightPickupDisclosure, PickupHoldReason } from "./FlightPickupFooter";
import { PickupTimeCard, PickupWorking } from "./PickupTimeCard";
import { SuggestedPickupCard } from "./SuggestedPickupCard";
import { ViewOnlyFieldset } from "./ViewOnlyFieldset";
import {
  LandingSource,
  flightDateFromPickup,
  flightLandingUsedIso,
  flightPickupCommitValue,
  flightWillArrive,
  formatFlightTime,
  isFlightPickupFrozen,
  landingTime,
  legTimeLabel,
  pickupFromFlight,
  resolveLanding,
  scheduledLegTime,
  utcIsoToAirportIso,
} from "./flightPickup";
import { useFlightCapture } from "./hooks/useFlightCapture";
import { useFlightDetails } from "./hooks/useFlightDetails";
import { useFlightTrackingEnabled } from "./hooks/useFlightTrackingEnabled";
import { departureWillDepart } from "./hooks/useSheetDepartureFlight";
import { STEP_MINUTES, pickupTimeZone, todayAtPickup, validatePickup, wallClockInstant } from "./pickupTime";
import { useForceValidate } from "./useForceValidate";

type WhoLayout = "card" | "sheet";

interface DateTimePanelProps {
  /** "card" = desktop side panel; "sheet" = mobile bottom sheet (adds a Close action). */
  layout: WhoLayout;
  onClose: () => void;
  /**
   * "card" layout only: called after Proceed commits the pickup so the wizard opens the next Trip
   * Details sub-step (Vehicle & Transfer). The "sheet" layout closes back to the list instead.
   */
  onAdvance?: () => void;
  /** When true, run validation immediately (the user was routed here by a failed exit). The fields now
   * start EMPTY, so an untouched panel names the missing date or time rather than showing nothing. */
  forceValidate?: boolean;
  /**
   * Take the client to the Address step, which owns the flight row — the "Update flight" link on a
   * cancelled or diverted flight. Without it the link opens the flight modal in place.
   */
  onUpdateFlight?: () => void;
}

const MERIDIEMS = ["AM", "PM"] as const;
type Meridiem = (typeof MERIDIEMS)[number];

/**
 * The backend's stored arrival columns, translated into the card's own vocabulary.
 *
 * Matched on the FAMILY rather than the exact key, for the same reason `appliedArrivalLabel` is: the
 * backend emits raw stored column names and a suffix change must not silently drop the kind. Two
 * tolerances for one column is how the client panel ends up unlabelled while the operator panel labels
 * the same booking correctly.
 *
 * A lookup rather than an object index, so a pathological value like `constructor` or `toString` cannot
 * return something off `Object.prototype` typed as a LandingSource. Anything unmatched is undefined
 * rather than guessed.
 *
 * The creation snapshot is the MAJORITY case, not an edge one — 34 of 46 seeded staging jobs have no
 * applied arrival and fall back to it. It maps to the runway `scheduled`, not `gateScheduled`: it comes
 * from Cirium Schedules with no tracked row, where every gate column is null, so claiming the gate family
 * would be false about where the value came from. Both label the row "Scheduled arrival", but the NOUN
 * differs — "scheduled arrival" against "scheduled gate arrival" — and the backend's own
 * `appliedArrivalNoun` says the former, which the two surfaces must not contradict on one booking.
 */
const STORED_ARRIVAL_SOURCES: [RegExp, LandingSource][] = [
  [/gate_actual/, "gateActual"],
  [/gate_estimated/, "gateEstimated"],
  [/gate_scheduled/, "gateScheduled"],
  [/at_booking|creation/, "scheduled"],
];

const toLandingSource = (source?: string | null): LandingSource | undefined =>
  (source && STORED_ARRIVAL_SOURCES.find(([pattern]) => pattern.test(source))?.[1]) || undefined;

const to12Hour = (h24: number) => String(((h24 + 11) % 12) + 1);
/** The inverse, for an hour already validated as 1–12. Shared so the touched-check and the branch that
 *  builds the pickup can never read the same fields as two different times. */
const to24Hour = (h12: number, meridiem: Meridiem) => (meridiem === "PM" ? (h12 % 12) + 12 : h12 % 12);
/** Strip to at most 2 digits and clamp to `max`, so an out-of-range time can't be typed in. */
const sanitize = (value: string, max: number) => {
  const digits = value.replace(/\D/g, "").slice(0, 2);
  return digits === "" ? "" : String(Math.min(parseInt(digits, 10), max));
};

/**
 * Centred 2-digit number field for hour / minute entry. Sized to Figma (80×90.5, 24px y-padding,
 * h2 text) and borrows TextInput's active treatment — border-primary + `shadow-input` glow on focus.
 */
const TimeField = ({
  value,
  onChange,
  onBlur,
  max,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  /** Largest value that can be typed (12 for hour, 59 for minute). */
  max: number;
  placeholder?: string;
  ariaLabel: string;
}) => (
  <input
    type="text"
    inputMode="numeric"
    value={value}
    placeholder={placeholder}
    aria-label={ariaLabel}
    onChange={(e) => onChange(sanitize(e.target.value, max))}
    onBlur={onBlur}
    className="h-20 min-w-0 flex-1 rounded-lg border border-neutral-mid-gray py-6 text-center text-2xl font-bold text-neutral-black focus:border-primary focus:shadow-input focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-neutral-surface-gray disabled:text-neutral-silver-gray max-lg:text-lg"
  />
);

/**
 * "Date and Time Pickup" — the Date/Time sub-panel of the Trip Details step. An inline calendar
 * (react-datepicker, themed via DateTimePanel.css) + an hour/minute/AM-PM entry. The selection is
 * committed to clientBookingSlice on Proceed (not live), matching the explicit button in Figma.
 */
export const DateTimePanel = ({ layout, onClose, onAdvance, forceValidate = false, onUpdateFlight }: DateTimePanelProps) => {
  const viewOnly = useBookingViewOnly();
  // Edit mode relabels the commit CTA "Confirm" (it closes the sub-panel rather than advancing).
  const isEdit = useAppSelector(selectEditJobId) != null;
  const dispatch = useAppDispatch();
  const committed = useAppSelector(selectPickupDatetime);
  // The operator's configured courtesy waiting minutes, for the hint under the delay input.
  const waitingTimes = useAppSelector(waitingTimesSelector);
  // An unparseable committed value is treated as no pickup at all. date-fns throws on an Invalid Date,
  // and this seeds both the calendar and the flight lookup's date, so an unguarded parse took the whole
  // panel down rather than degrading to manual entry.
  const committedDate = useMemo(() => {
    if (!committed) return null;
    const parsed = new Date(committed);
    return isNaN(parsed.getTime()) ? null : parsed;
  }, [committed]);
  // The FLIGHT's date — the day to ask Cirium about, which is NOT the pickup's day. A late landing plus a
  // wait crosses midnight (23:45 + 30 → 00:15 the next day), so reading the lookup date off the derived
  // pickup asks about a day the flight never flew: it stops resolving, the card claims it cannot find a
  // perfectly good flight, and no landing is sent for precisely the late-arrival bookings the provenance
  // exists for.
  //
  // Recovered by subtracting the booked wait from the booked pickup, which is the airport-local wall clock
  // of the arrival the pickup was built on. That is the same date `flight_arrival_scheduled_at_booking`
  // gives when converted to the arrival airport's zone — it IS that arrival, read through the pickup
  // instead of through a timezone the client does not have here. The stored arrival reaches the client as
  // `flight_pickup_derived_arrival`, but in UTC, and the arrival airport's zone only arrives WITH the
  // lookup this date is needed to make; `pickup_time` carries the offset, but `viewDraft` normalises it to
  // a naive wall clock before the panel sees it. So the two agree wherever the stored arrival is the
  // pickup's actual operand, and the pickup is the only one of the pair reachable from here.
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  const pickupNotFlying = useAppSelector(selectPickupNotFlying);
  // Seeded from the committed delay so replaying an existing flight booking shows the delay the
  // client actually booked; without it the panel re-derived `pickup = landing + 0` on Confirm and
  // silently clobbered the booked time (DEV-1429). Committed on Proceed, like the date/time fields.
  const committedDelay = useAppSelector(selectPickupFlightDelayMinutes);
  const committedFlightDate = useMemo(
    () => (committed && pickupFlightNumber ? flightDateFromPickup(committed, committedDelay) : null),
    [committed, pickupFlightNumber, committedDelay]
  );

  /**
   * Every field replays the COMMITTED pickup, and is otherwise EMPTY.
   *
   * There used to be a fallback — `defaultPickupSlot()`, the current local time on a 5-minute grid, seeded
   * into all four so Proceed was actionable the moment the panel opened. That is what this removes. "Now"
   * is almost never the journey being booked, and a pre-filled answer is accepted by not being noticed:
   * the client confirms a time they never chose. An empty field asks the question instead of answering it.
   *
   * THE MERIDIEM TOO, and it is the one that is easy to miss: a two-button toggle with AM already lit is
   * half the answer pre-filled, and the half that books a trip twelve hours out.
   *
   * Only the CREATE path is affected. An edit, a re-opened panel after a Proceed, and a view-only replay
   * all arrive with a committed pickup and still show what was actually booked.
   *
   * The calendar starts on the flight's date when there is one — in flight mode it IS the flight-date
   * picker, and showing the pickup's date would put a different day above the flight card's own legs.
   */
  const [date, setDate] = useState<Date | null>(() => {
    if (!committedFlightDate) return committedDate;
    // Parsed as a LOCAL midnight, matching how `format(date, "yyyy-MM-dd")` reads it back out.
    const parsed = new Date(`${committedFlightDate}T00:00:00`);
    return isNaN(parsed.getTime()) ? committedDate : parsed;
  });
  const [hour, setHour] = useState(() => (committedDate ? to12Hour(committedDate.getHours()) : ""));
  const [minute, setMinute] = useState(() => (committedDate ? String(committedDate.getMinutes()).padStart(2, "0") : ""));
  const [meridiem, setMeridiem] = useState<Meridiem | null>(() => (committedDate ? (committedDate.getHours() >= 12 ? "PM" : "AM") : null));
  /**
   * The client has typed a time since the last DEPARTURE-flight change (DEV-1487) — so the SUGGESTED
   * PICKUP must not overwrite it.
   *
   * Its own flag rather than `timeTouched`, which compares the fields against the COMMITTED pickup and
   * so cannot tell a hand edit from the pre-fill itself: the pre-fill writes these same fields, and on
   * the create path there is no committed pickup to compare against at all. Set only by the controls a
   * human presses — never by `snapHour`/`snapMinute`, which normalise on blur, nor by the pre-fill.
   */
  const [timeHandEdited, setTimeHandEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Retract a validation message the moment the client touches the thing it was about (DEV-1619).
   *
   * `error` is only ever WRITTEN by `resolvePickup`, at Proceed or a forced validate — so without this it
   * survives every edit that fixes it: "Pickup time can't be in the past" sits there while the client
   * picks a date next month, which reads as the panel refusing an answer it would in fact accept.
   *
   * Clearing rather than re-validating on each keystroke is deliberate: re-running `resolvePickup` mid-edit
   * reports a half-typed hour as invalid, so the panel would be arguing with someone who is still talking.
   * Proceed re-validates against a fresh `now` regardless, which is where the real answer comes from.
   */
  const clearError = () => setError(null);
  const editHour = (value: string) => {
    setTimeHandEdited(true);
    clearError();
    setHour(value);
  };
  const editMinute = (value: string) => {
    setTimeHandEdited(true);
    clearError();
    setMinute(value);
  };
  const editMeridiem = (value: Meridiem) => {
    setTimeHandEdited(true);
    clearError();
    setMeridiem(value);
  };
  // The disrupted panel's way out — the same modal the Address step opens, resolved through the same hook
  // so the two call sites cannot drift on when a committed pickup is thrown away.
  const [flightModalOpen, setFlightModalOpen] = useState(false);
  const { confirmFlightNumber, notFlying } = useFlightCapture();

  // Airport pickup with a captured flight number: resolve the flight for the selected date and,
  // when it lands on a known time, derive the pickup from landing + a driver-arrival delay
  // instead of manual time entry. Re-resolves automatically whenever the calendar date changes.
  // The flight-tracking add-on. Gated on `enabled` alone, never `enabled || isResolving`: the lookup is
  // the expense being gated, and an operator that may not have the add-on must not pay for one. The hook
  // carries the reasoning, including why unknown reads as OFF and what makes that safe.
  const { enabled: flightTrackingEnabled, isResolving: isFlightTrackingResolving } = useFlightTrackingEnabled();

  const pickup = useAppSelector(selectPickup);
  // Typed times are wall clocks AT THE PICKUP, so "now" and "today" are measured in its zone rather than the
  // booker's browser (DEV-1857). Null until there is a pickup, which keeps the browser-local behaviour.
  const pickupZone = pickupTimeZone(pickup);
  // The landing the COMMITTED pickup was derived from, so the anchored branch can hand the booked triple
  // back untouched instead of re-deriving two thirds of it.
  const committedLandingUsed = useAppSelector(selectPickupFlightLandingUsed);
  const [delayMinutes, setDelayMinutes] = useState(() => String(committedDelay ?? 0));
  // Empty or unparseable reads as 0 — the field's own default, and what the blur handler restores.
  const delayMin = useMemo(() => {
    const parsed = parseInt(delayMinutes, 10);
    return isNaN(parsed) ? 0 : parsed;
  }, [delayMinutes]);
  // Compared against the committed FLIGHT date in flight mode, not the pickup's. The calendar now holds the
  // flight's date, and a pickup pushed past midnight by the wait sits on the following day — so comparing
  // the two would read an untouched late-arrival booking as date-edited, re-deriving its pickup off the
  // live flight on every open and defeating the anchoring DEV-1499 exists to hold.
  const dateTouched =
    committedFlightDate && date
      ? format(date, "yyyy-MM-dd") !== committedFlightDate
      : !committedDate || !date || format(date, "yyyy-MM-dd") !== format(committedDate, "yyyy-MM-dd");

  // Hour, minute and meridiem against the COMMITTED pickup's own time. `dateTouched` cannot carry this:
  // it compares `yyyy-MM-dd` and nothing else, by design. Without a time term a client who changed ONLY the
  // time left every operand reading untouched — the anchored branch in `resolvePickup` fired, handed the
  // booked pickup straight back, and the typed value was dropped with no error shown (DEV-1507). Not a
  // flight-only defect: that branch is not gated on a flight, so an ordinary booking hits it too.
  //
  // Equality with the SEED has to read as UNTOUCHED. The fields are seeded from the committed pickup, so a
  // term that fired on open would re-derive on every open and defeat exactly the anchoring DEV-1499 exists
  // to hold — silently moving an unedited booking to wherever the flight has since drifted. Hence the
  // comparison is against `committedDate`, which IS what the fields are seeded from, and against the
  // parsed 24-hour pair rather than the raw strings, so an "09" typed over a seeded "9" is not an edit.
  //
  // The RAW minute, never the grid-snapped one: a booked pickup can sit off the 5-minute grid — clamped to
  // MIN_LEAD_MINUTES by the engine, or hand-edited by an operator — and seeds an off-grid minute that
  // snapping before comparing would read as edited the moment the panel opened.
  //
  // An unparseable field reads as TOUCHED, which is the safe direction: it falls through to the manual
  // branch and the client is told "Enter a valid hour (1–12)", rather than a half-typed time silently
  // committing the booked one. With no committed pickup the term is moot — `!committedDate` already opens
  // `rederive` on the create path, which this defect never reached.
  //
  // An UNSET meridiem is the same case and reads the same way. It cannot occur while a pickup is committed
  // — the meridiem is seeded from it — so this only ever guards the type; it is stated rather than cast
  // because a half-answered time must never resolve to a 24-hour reading nobody selected.
  const typedHour = parseInt(hour, 10);
  const typedMinute = parseInt(minute, 10);
  const typedTimeIsValid = !!meridiem && !isNaN(typedHour) && typedHour >= 1 && typedHour <= 12 && !isNaN(typedMinute);
  const timeTouched =
    !!committedDate &&
    (!typedTimeIsValid ||
      !meridiem ||
      to24Hour(typedHour, meridiem) !== committedDate.getHours() ||
      typedMinute !== committedDate.getMinutes());

  // Stored, so it needs no flight lookup — which is the whole point. The lookup is skipped in view-only
  // to stop RECOMPUTATION, and that skip must not take disclosure with it: a closed booking on a
  // cancelled flight still has to say so, and most bookings with a disruption are closed.
  //
  // A different DATE is a different flight instance, so the reason no longer describes what is on screen.
  // A changed wait is not: the flight is the same one and a cancellation is still true. A changed flight
  // NUMBER clears it at the source, in the slice.
  const frozenReason = useAppSelector(selectPickupFlightFrozenReason);
  const storedDisruption = flightDisruptionReason(dateTouched ? null : frozenReason);

  const flightDate = date ? format(date, "yyyy-MM-dd") : null;
  // Scope-aware: authed → account-scoped client lookup, guest → provider-scoped public twin. Same
  // FlightDetails contract either way; the inactive scope's query is skipped inside the hook.
  const {
    data: flight,
    isFetching: isFlightFetching,
    isError: isFlightError,
  } = useFlightDetails({
    flightNumber: pickupFlightNumber ?? "",
    date: flightDate ?? "",
    airport: extractAirportIata(pickup) ?? undefined,
    // NO view-only skip (DEV-1510). A non-editable booking used to be skipped here unless it carried a
    // stored disruption, which left a reader of a perfectly ordinary flight booking looking at a bare
    // calendar: no card, no flight number, no delay. Staging J000472 (QF2) showed only the calendar.
    //
    // The skip was justified by "a closed booking must never render a live-derived WORKING" — the pickup is
    // frozen while the flight keeps moving, so a recomputed one fails to add up, and two people opening the
    // same job on different days see different arithmetic for the same unchanged time. That rule still
    // holds, and is still worth every word of this comment. What changed is WHERE it is enforced: at
    // `rederive`, which is false for a reader, so the card renders the FROZEN arrival and the anchored
    // branch hands the booked pickup straight back. Enforcing it by withholding the data instead also
    // withheld the disclosure, and the two are not the same thing.
    //
    // Established behaviour, not new risk: the disrupted path has fetched on non-editable bookings since
    // DEV-1509 — it leads with the card, and a warning with no card is an assertion with nothing behind it.
    // This is the ordinary case being made consistent with it.
    //
    // The flight-tracking add-on is NOT repeated here: `useFlightDetails` gates on it itself, so there is
    // one rule protecting the request rather than one per caller. `flightTrackingEnabled` below is still
    // this panel's, for what it RENDERS and for holding Proceed through the guest resolve window.
    skip: !pickupFlightNumber || !flightDate,
  });
  // A pickup is only flight-derivable when the flight will actually arrive as looked up. Shared with the
  // sheet-level re-derivation so the same booking cannot derive on one surface and not the other.
  const landing = !isFlightFetching && !isFlightError && flightWillArrive(flight) ? landingTime(flight) : null;

  // The drop-off's own lookup, in the DEPARTING direction (DEV-1487). Same airport extractor and the
  // same pickup coordinates as `useSheetDepartureFlight`, so the two subscribers share one RTK Query
  // entry — WHENEVER THEY AGREE ON THE DATE, which is the common case and not a guarantee. That hook
  // reads the committed pickup's day; this panel reads the calendar's, which the client can move, and
  // which in flight mode is seeded from pickup − wait and so lands a day earlier on a booking whose
  // wait crosses midnight. Where they part, the cost is a second billed departing lookup and a
  // terminal swap acting on a different day's flight than the card on screen shows. Closing it needs
  // the two to agree on one date; that is DEV-1487 follow-up, not something this panel can decide.
  //
  // The gate is the add-on's, applied inside `useFlightDetails`, exactly as the arrival lookup's is.
  const departureFlightNumber = useAppSelector(selectDepartureFlightNumber);
  const destination = useAppSelector(selectDestination);
  const pickupTimeSource = useAppSelector(selectPickupTimeSource);
  const departureFlightChangedSinceCommit = useAppSelector(selectDepartureFlightChangedSinceCommit);
  const {
    data: departureFlight,
    isFetching: isDepartureFetching,
    isError: isDepartureError,
  } = useFlightDetails({
    flightNumber: departureFlightNumber ?? "",
    date: flightDate ?? "",
    airport: extractAirportIata(destination) ?? undefined,
    direction: "departing",
    // Undefined rather than 0 for a missing pickup, so the server omits TRAVEL TIME instead of
    // measuring the drive from a null island.
    pickupLat: pickup?.lat ?? undefined,
    pickupLng: pickup?.lng ?? undefined,
    // `viewOnly` for the reason `useSheetDepartureFlight` carries it: everything downstream requires
    // `!viewOnly`, so a reader would fire a Cirium call AND a server-side distance lookup whose result
    // can never reach the screen.
    skip: !departureFlightNumber || !flightDate || viewOnly,
  });
  // Only a flight that is actually going carries a usable suggestion — same reasoning as
  // `flightWillArrive` on the arrival side, and the same helper the drop-off terminal swap uses.
  //
  // `isError` for the reason the terminal swap weighs it: RTK Query retains the last good `data`
  // through a failed refetch, and a card that goes on asserting a suggestion the server has stopped
  // confirming is worse than no card.
  const departureResolved =
    !isDepartureFetching && !isDepartureError && departureWillDepart(departureFlight) && !!departureFlightNumber
      ? departureFlight
      : undefined;

  /**
   * SUGGESTED PICKUP as a wall clock the time fields can hold.
   *
   * `recommendedPickupAt` is UTC (contract §1) while these fields are a naive wall clock, so it is read
   * through the DEPARTURE AIRPORT's zone — the same conversion `storedLandingIso` makes for the arrival
   * side, and for the same reason: read as a bare UTC clock, a Sydney pickup renders ten hours out.
   *
   * The departure airport's zone rather than the pickup's, because the pickup address has no timezone
   * anywhere on the draft and the two are a car journey apart — the drive that TRAVEL TIME measures.
   */
  const departureTimezone = departureResolved?.departure?.airport?.timezone;
  const suggestedPickupIso = useMemo(
    () =>
      departureResolved?.recommendedPickupAt && departureTimezone
        ? utcIsoToAirportIso(departureResolved.recommendedPickupAt, departureTimezone)
        : null,
    [departureResolved?.recommendedPickupAt, departureTimezone]
  );
  /** True when the pickup is being derived from the flight rather than typed in manually. */
  const isFlightDerived = !!(pickupFlightNumber && landing);

  /**
   * An ARRIVAL flight owns the pickup time, so the departure may not propose one.
   *
   * Kevin, settled on mobile and applied to both surfaces: BOTH flight cards are shown — a booking with
   * a flight at each end has two flights worth reading — but the SUGGESTED PICKUP appears only when no
   * arrival flight is in play. With an arrival captured the pickup is landing + delay, and a second
   * card proposing a different time beside it is two answers to one question. Without one — a
   * non-airport pickup, or an airport pickup answered "Not Flying" — the suggestion is the only offer
   * on the panel and supplies the time.
   *
   * Keyed on the NUMBER, not on a resolved landing. A captured flight that has not resolved — the
   * lookup failed, or it is not found — still means the client told us they are landing here, so the
   * departure must not quietly take the pickup over while the arrival is merely unavailable.
   *
   * The departure is CAPTURED AND STORED regardless: the drop-off's flight row, the terminal swap,
   * `departure_flight_number` on the wire and the summary row are untouched by this.
   */
  const arrivalOwnsPickup = !!pickupFlightNumber;

  /** The departure card — informational, and shown whenever the drop-off's flight resolves. */
  const showDepartureCard = !!departureResolved && !!departureFlightNumber && flightTrackingEnabled && !viewOnly;

  /**
   * Is a SUGGESTED PICKUP on offer? THE one predicate — it both renders the card and licenses the
   * pre-fill, so the thing on screen and the thing that writes can never disagree about whether a
   * suggestion exists.
   *
   * `suggestedPickupIso` rather than the raw `recommendedPickupAt`, so we never offer a value the
   * picker could not receive (an airport with no timezone yields no wall clock).
   */
  const departureSuggestionOffered = showDepartureCard && !!suggestedPickupIso && !arrivalOwnsPickup;

  /**
   * Whether the SUGGESTED PICKUP may write itself into the time fields.
   *
   * Built ON `departureSuggestionOffered` rather than restating its terms, so the pre-fill cannot
   * write a suggestion the panel is not showing — which is exactly how an arrival-derived pickup would
   * get quietly overwritten by a card nobody can see.
   *
   * A HAND EDIT IS KEPT, from this session (`timeHandEdited`) or a previous one (`pickupTimeSource`
   * hydrated as "manual"). Only a flight CHANGE at either end reopens it, by resetting both — the
   * slice nulls `pickupTimeSource`, and the effect below resets the local flag.
   */
  const suggestionApplies = departureSuggestionOffered && !timeHandEdited && pickupTimeSource !== "manual";

  // The flight-derived pickup is ANCHORED to what was booked, not recomputed from the live arrival on
  // every Confirm. Otherwise confirming an existing booking unchanged moves the pickup to wherever the
  // flight has drifted — and since the server will not re-snapshot an unchanged flight, the frozen
  // arrival stays put while the pickup moves, leaving `derived_arrival + wait ≠ pickup_time`.
  // Re-deriving against a moved flight is a deliberate act with its own confirmation (D5), not a side
  // effect of confirming an unrelated edit.
  //
  // So we re-derive only from operands the client controls here: the wait, the date, or the flight
  // itself (a flight change clears the committed pickup in LocationPanel, landing us in `!committed`).
  // Compare the parsed minutes, not the raw strings — the input permits leading zeros, and "045"
  // against a committed 45 is the same wait, not an edit.
  // A NEW FLIGHT is an operand too, and was missing. Without it, changing QF2 to QF8 and confirming
  // committed the old pickup — derived from QF2's arrival — while the card above showed QF8's legs.
  // `anchoredWorkingHolds` did not catch it either: the stored arrival was still QF2's, so the working
  // appeared to add up against a flight that was no longer selected.
  //
  // Gated on a RESOLVED landing for the new flight: a freshly typed number is briefly present with no
  // lookup settled, and flipping then would rebuild the pickup off no arrival at all.
  //
  // Two baselines, because neither covers both paths. `editOriginal` is the booking as loaded, so it catches
  // a flight changed at any point in an edit session — but it does not exist on CREATE. The slice flag is
  // set by the change itself, so it covers create, and it is what replaced nulling `pickupDatetime` for this
  // job (DEV-1510) — that nulling forced the re-derivation by destroying the committed pickup, and took the
  // booked date with it.
  const originalFlightNumber = useAppSelector(selectEditOriginal)?.pickupFlightNumber ?? null;
  const flightChangedSinceCommit = useAppSelector(selectPickupFlightChangedSinceCommit);
  const flightTouched =
    !!landing && (flightChangedSinceCommit || (!!originalFlightNumber && !isSameFlightNumber(pickupFlightNumber, originalFlightNumber)));
  const delayTouched = delayMin !== (committedDelay ?? 0);
  // The disruption as ONE fact, whichever signal carried it: the booking's stored freeze reason first,
  // else the live lookup's status. They normally agree, and the stored one wins because it is the reason
  // the engine actually acted on.
  //
  // PRESENTATION ONLY. Everything below that decides a pickup reads `storedDisruption`, unchanged — a live
  // cancelled or diverted status already yields no landing to derive from, so folding it in here cannot
  // move a derivation.
  const liveDisruption = flight?.status === "cancelled" || flight?.status === "diverted" ? flight.status : null;
  const disruptedAs = storedDisruption ?? liveDisruption;
  const isDisrupted = !!pickupFlightNumber && !!disruptedAs;

  // Live GATE landing against the one the booked pickup was built on — both gate, so they are the same
  // quantity measured at two times. (The card's leg row is the RUNWAY chain and is a different event
  // entirely; it needs nothing from this.)
  //
  // The stored value is UTC while every other flight time here carries the airport's own offset, so it is
  // converted through the arrival airport's timezone rather than read as a wall clock.
  const derivedArrival = useAppSelector(selectPickupFlightDerivedArrival);
  const storedLandingIso = useMemo(() => {
    const zone = flight?.arrival?.airport?.timezone;
    return derivedArrival && zone ? utcIsoToAirportIso(derivedArrival, zone) : null;
  }, [derivedArrival, flight?.arrival?.airport?.timezone]);
  // Requiring a STORED arrival makes drift an edit-path question by construction:
  // `pickupFlightDerivedArrival` is written in exactly one place — `mapBookingDetailToViewDraft` —
  // `rebookDraft` nulls the flight fields outright, and `clientBooking` is not in the persist whitelist, so
  // no create-path draft can carry one. On create there is simply the live flight and the pickup it
  // produces, which needs no comparison.
  const liveLanding = !storedDisruption && landing ? landing : null;
  const driftedAtAll = !!liveLanding && !!storedLandingIso && Date.parse(liveLanding) !== Date.parse(storedLandingIso);
  // Drift is an operand too, and ANY drift re-derives — there is NO materiality threshold on this path
  // (DEV-1531, superseding D4/D5 on DEV-1490 for the edit path). The 15/30 rule is a churn control for the
  // ENGINE, which wakes repeatedly against a flight that moves all day; an edit is one deliberate human
  // save, so the churn it prevents does not exist here. `flightUpdateIsMaterial` stays in `flightPickup.ts`
  // for the engine's own rule — only this caller drops it. The pickup therefore re-derives from the arrival
  // the card is already showing, so the two can never disagree at any size of drift, and a pickup that
  // MOVED is disclosed on the change-review diff before the client confirms.
  //
  // Still gated on the re-derived pickup being COMMITTABLE, which is not pedantry and is NOT the threshold:
  // drift is the one operand the client cannot move. A flight that landed early enough puts landing + wait
  // in the past, and re-deriving onto it would hand the client "Pickup time can't be in the past" on an edit
  // they did not make, with no lever on this panel to clear it — `resolvePickup`'s anchor branch skips
  // validation precisely to stop an elapsed pickup blocking an unrelated edit. Declining to re-derive keeps
  // that escape open AND keeps the pair together: the card goes on showing the frozen landing, which is
  // still the one the held pickup was built on.
  const driftRederives = driftedAtAll && !validatePickup(pickupFromFlight(liveLanding!, delayMin));

  // NEVER for a reader (DEV-1510). Every operand above is one the client moved — except `driftRederives`,
  // which is computed purely from the lookup, and the lookup now runs on non-editable bookings too. Left
  // ungated, a closed booking whose flight has since drifted would flip `rederive` on its own, null
  // `storedArrival`, and put the LIVE arrival on the card beside a pickup that was built from the frozen one
  // — the exact "live-derived working on a closed booking" the skip above used to prevent. Nothing can write
  // it back (there is no Proceed in view-only, and `useSheetFlightPickup` gates itself on viewOnly), so this
  // is about what is DISPLAYED, which is where the guarantee actually lives now.
  // A new DEPARTURE flight is an operand too (DEV-1487): the committed pickup may be the previous
  // flight's SUGGESTED PICKUP, so it has to reopen the same way a new arrival flight does. Gated on a
  // resolved suggestion for the same reason `flightTouched` is gated on a resolved landing — a freshly
  // typed number is briefly present with no lookup settled — and on the arrival side not deriving,
  // which outranks it.
  const departureSuggestionTouched = !!suggestedPickupIso && suggestionApplies && departureFlightChangedSinceCommit;
  const rederive =
    !viewOnly &&
    (!committedDate || delayTouched || dateTouched || timeTouched || flightTouched || driftRederives || departureSuggestionTouched);
  // The anchored branch's own condition, hoisted: `handleProceed` needs it too, so the visible-minute
  // snap does not fire on a pickup that branch handed back untouched. See there for why that matters.
  const isAnchored = !rederive && !!committed;

  // D14: the arrival on screen must be the one the pickup on screen was derived from, and the pair moves
  // together. While the pickup is the booked value we show the arrival it was actually built from — the
  // stored one, frozen; the moment the pickup re-derives — an operand the client controls moved, or the
  // flight itself drifted materially — the arrival shown becomes that same live value.
  //
  // Which is the whole disclosure story on this card: the pickup is never derived from a landing other than
  // the one beside it, so the card has no discrepancy to explain. A pickup that MOVED is disclosed on the
  // change-review diff instead, stated as a before → after and gating the save.
  const storedArrival = rederive ? null : storedLandingIso;

  // D11a is unconditional: a working line reconciles exactly against the pickup beside it, or it is not
  // rendered at all. On the re-derived branch it reconciles by construction — same operands, same call.
  // On the ANCHORED branch it only holds if the stored arrival and the stored pickup actually agree, and
  // they can disagree today: PickupTimeCalculator clamps a pickup to MIN_LEAD_MINUTES when a callback
  // lands moments before it, an operator can hand-edit the pickup, and the wait can change after the
  // last engine write. D14 asserting the pair reconciles is design intent, not a runtime guarantee, and
  // this is the surface a paying customer reads — so check it rather than assume it.
  //
  // Compared as rendered wall clocks, which is what the reader checks. A booking with no stored arrival
  // cannot reconcile at all: its pickup is the booked value while the card shows a live arrival, so it
  // shows no working either.
  const wallClock = (d: Date) => format(d, "yyyy-MM-dd HH:mm");
  /** Does `arrival + delay` land on the booked pickup's wall clock — the one D11a check, shared by every working. */
  const reconcilesWithBooked = (arrivalIso: string | null | undefined, delay: number): boolean =>
    !!arrivalIso && !!committedDate && wallClock(flightPickupCommitValue(arrivalIso, delay)) === wallClock(committedDate);
  const anchoredWorkingHolds = reconcilesWithBooked(storedArrival, delayMin);

  // Passed to the card as `{ iso, source }`. `source` tells the card whether its own runway row names a
  // different event from the one in the footer. The stored source arrives as the backend's raw column name,
  // so it is translated here — the card's vocabulary is LandingSource and it should not learn a second one.
  //
  // An unrecognised or absent value maps to undefined rather than to a guess: not knowing which kind of
  // arrival the pickup was built on is the honest answer, and every consumer already handles it.
  const derivedArrivalSource = useAppSelector(selectPickupFlightDerivedArrivalSource);
  const frozenSource = toLandingSource(derivedArrivalSource);
  const frozenArrival = anchoredWorkingHolds && storedArrival ? { iso: storedArrival, source: frozenSource } : null;

  // The edit path carries NO threshold, on the backend or here: the pickup rebuilds from the live gate
  // landing by the full drift however small.
  //
  // Still gated on `delayTouched`, which since DEV-1531 no longer tracks when the pickup moves: a drifted
  // booking has ALREADY re-derived before anyone touches the wait, so this names the new time only if they
  // happen to. The primary disclosure is the change-review diff on Update Booking, which fires either way;
  // this button label is the secondary one and now under-reports. Widening it to `driftRederives` is a
  // separate call — see DEV-1531.
  const editedDriverArrival = driftedAtAll && delayTouched && liveLanding ? flightPickupCommitValue(liveLanding, delayMin) : null;

  // Why the pickup is being typed rather than derived — one sentence, one fact, ordered so the fact we
  // hold with most certainty wins. A cancelled flight is usually pulled from the schedule, so the lookup
  // that fails is often failing BECAUSE of the disruption; leading with "we couldn't find it" would report
  // the symptom and drop the cause.
  //
  // View-only drops the call to action. Its fields are a read-only replay of the booked time, so telling
  // that reader to enter one points at a control the closed booking will not let them use.
  const noticeSuffix = viewOnly ? "." : " — enter your pickup time instead.";
  /**
   * NO DATE YET is not a failed lookup, and must not be reported as one.
   *
   * The calendar now opens empty, so `flightDate` is null until the client picks a day and the lookup is
   * skipped rather than attempted. Every string below describes a lookup that RAN — leading with "we
   * couldn't find flight QF994 on that date" before any date exists blames the client's flight number for
   * the panel's own empty field, on the very first thing they see.
   *
   * First in the chain, and safe there: a disruption reaches this state through `storedDisruption`, which
   * is nulled by `dateTouched` — and `dateTouched` is true whenever `date` is null. So `isDisrupted` is
   * false with no date, and the disrupted branch cannot render this string.
   */
  const flightNotice = !flightDate
    ? `Select a date above and we'll look up flight ${pickupFlightNumber}.`
    : isFlightError
    ? `The flight lookup is unavailable right now${noticeSuffix}`
    : flight && flight.status !== "not_found"
    ? `Flight ${pickupFlightNumber} is ${flight.status}${noticeSuffix}`
    : `We couldn't find flight ${pickupFlightNumber} on that date${noticeSuffix}`;

  /**
   * The drop-off twin of {@link flightNotice} (DEV-1619). `departureResolved` is null on a failed lookup,
   * a not-found number and a flight that will not depart — and until now each of those rendered NOTHING,
   * so a client who mistyped their departure flight got the same blank panel as one who had not entered
   * a flight at all.
   *
   * The CALL TO ACTION differs from the arrival's, and that is the whole reason this is a separate string
   * rather than a parameterised one. An unresolved ARRIVAL costs the client their pickup time, so its copy
   * sends them to the time fields. A departure never owned the pickup — it only ever OFFERS a suggestion —
   * so the honest thing to say is that the flight is kept and the suggestion is not coming. Mirrors the
   * rider app's departure copy, so the two surfaces make the same promise.
   */
  const departureNotice = !flightDate
    ? `Select a date above and we'll look up flight ${departureFlightNumber} to suggest a pickup time.`
    : isDepartureError
    ? `The flight lookup is unavailable right now — flight ${departureFlightNumber} is saved, but we can't suggest a pickup time.`
    : departureFlight && departureFlight.status !== "not_found"
    ? `Flight ${departureFlightNumber} is ${departureFlight.status} — we can't suggest a pickup time for it.`
    : `We couldn't find flight ${departureFlightNumber} on that date — it's saved, but we can't suggest a pickup time.`;

  /**
   * Pre-fill the time picker with the SUGGESTED PICKUP, once per (flight, suggestion).
   *
   * Written into the FIELDS rather than committed to the draft, because the suggestion is a starting
   * point the client may overwrite before pressing Proceed — committing it here would move a booked
   * time behind someone who opened this panel to look at the calendar.
   *
   * Only the TIME. The trip DATE survives a flight change (user story 10, same rule as DEV-1510 for
   * pickups): the client chose that day, and a replacement flight is not a reason to send the calendar
   * anywhere.
   *
   * Keyed on the flight number as well as the instant, so re-entering the SAME number re-applies
   * nothing while a genuinely different flight that happens to recommend the same clock still does.
   */
  const appliedSuggestionKey = useRef<string | null>(null);
  useEffect(() => {
    if (!suggestionApplies || !suggestedPickupIso) return;
    const key = `${departureFlightNumber}|${suggestedPickupIso}`;
    if (appliedSuggestionKey.current === key) return;
    // Through `flightPickupCommitValue`, so the fields hold the AIRPORT's wall clock re-encoded
    // browser-local — the same naive-wall-clock convention the derived and manual paths commit in.
    // Reading the offset ISO's own local fields would render a Sydney pickup in the booker's zone.
    const suggested = flightPickupCommitValue(suggestedPickupIso, 0);
    if (isNaN(suggested.getTime())) return;
    // A suggestion on ANOTHER DAY is not applied at all. Only the time is written, so a red-eye —
    // 00:30 departure, 3 h lead, 1 h drive, recommending 20:30 the PREVIOUS evening — would put 8:30 PM
    // on the flight's own day: twenty hours after the aircraft left, and past nothing `validatePickup`
    // checks. Writing the date instead would move the day the client chose, which is the one thing the
    // calendar must not do (user story 10). So the card still shows the working and they set the time.
    if (date && format(suggested, "yyyy-MM-dd") !== format(date, "yyyy-MM-dd")) return;
    appliedSuggestionKey.current = key;
    setHour(to12Hour(suggested.getHours()));
    setMinute(String(suggested.getMinutes()).padStart(2, "0"));
    setMeridiem(suggested.getHours() >= 12 ? "PM" : "AM");
  }, [suggestionApplies, suggestedPickupIso, departureFlightNumber, date]);

  // A different departure flight is a different suggestion, so the hand edit that refused the previous
  // one describes a flight no longer on the booking. The slice nulls `pickupTimeSource` on the same
  // comparison; this is its panel-local half.
  //
  // The applied key goes too, or switching BACK to a flight already seen this session leaves the other
  // flight's time in the picker: the recomputed key matches the stale one, the effect above returns
  // early, and Proceed commits QF2's clock as QF994's pickup — recorded as "suggested", which it is not.
  //
  // The ARRIVAL number is a dependency for the same reason: capturing one flips the booking out of the
  // departure's case and clearing one ("Not Flying") flips it back in, and the suggestion has to be
  // re-evaluated across both. Without it, a hand edit made before "Not Flying" would go on refusing a
  // suggestion the client is only now being offered.
  useEffect(() => {
    setTimeHandEdited(false);
    appliedSuggestionKey.current = null;
  }, [departureFlightNumber, pickupFlightNumber]);

  /**
   * Normalise the hour to 1–12 when the field loses focus (0 → 12).
   *
   * An EMPTY field is LEFT empty. Filling it on blur would put an hour nobody typed into a
   * field the panel deliberately starts blank — tabbing past it would be enough to book 12 o'clock, which
   * is the same silent wrong-time booking the empty default exists to prevent, arrived at differently.
   * The minute keeps its blur fill below: ":00" against a typed hour is the client's own "9 o'clock",
   * while an hour conjured from nothing is not an answer they gave.
   */
  const snapHour = () => {
    if (hour === "") return;
    const h = parseInt(hour, 10);
    setHour(isNaN(h) || h < 1 ? "12" : String(Math.min(h, 12)));
  };

  /** Round the typed minute onto the 5-min grid when the field loses focus. */
  const snapMinute = () => {
    // Not while the pickup is anchored. A booked pickup can sit off the grid — clamped to MIN_LEAD_MINUTES,
    // or hand-edited — so rounding its seeded minute would rewrite a field nobody typed in, and
    // `timeTouched` would read that as an edit: the anchor releases on a focus and a blur, and the booked
    // pickup, its wait and its landing are replaced by a value the client never entered.
    if (isAnchored) return;
    if (minute === "") return setMinute("00");
    const m = parseInt(minute, 10);
    if (isNaN(m)) return;
    setMinute(String(Math.min(55, Math.round(m / STEP_MINUTES) * STEP_MINUTES)).padStart(2, "0"));
  };

  // Resolve the committable pickup ISO from the current inputs, or an error message. No side effects,
  // so both Proceed (which commits) and validation display (which only shows the error) can share it.
  //
  // The pickup, the wait and the landing travel TOGETHER rather than being recomputed by the caller,
  // because the server verifies them as one claim: `landing + delay*60 === pickup`, to the second. Each
  // branch therefore states all three, and only the branch that actually builds a pickup out of a landing
  // reports one — reading `landing` again at the call site would attach the LIVE arrival to a pickup taken
  // from somewhere else, which is precisely the pair the server refuses.
  const resolvePickup = (): { error: string } | { value: string; delayMinutes: number | null; landingUsed: string | null } => {
    if (!date) return { error: "Please select a date" };

    // Nothing the client controls has moved: commit the booked pickup back unchanged. Deliberately skips
    // validatePickup — re-validating an already-booked pickup would block an unrelated edit on a booking
    // whose lead time has since elapsed.
    //
    // Keyed on the ANCHOR, not on the flight lookup. With tracking off there is no lookup and so no
    // landing, which used to drop an untouched flight booking into the manual branch below — where
    // validatePickup rejects a pickup that has since elapsed and the edit could not be saved at all.
    // The booked pickup handed back unchanged — so its companions are handed back unchanged too. Clearing
    // either of them here is not a no-op: re-opening this row and pressing Proceed again lands on this
    // branch, and dropping the wait leaves the server holding `pickup != arrival + wait` on a job the
    // client only meant to look at, while dropping the landing quietly reverts the booking to letting the
    // server resolve an arrival of its own. `committedDelay` rather than `delayMin` keeps a never-asked
    // null distinct from a deliberate 0.
    if (isAnchored && committed) return { value: committed, delayMinutes: committedDelay, landingUsed: committedLandingUsed };

    // Flight-derived pickup: landing time + "delay driver arrival by" minutes. The landing carries
    // its own date (an overnight flight can land the day after the selected flight date).
    if (pickupFlightNumber && landing) {
      // Validate the true landing+delay instant against now; commit the airport-local wall clock
      // (re-encoded browser-local) so the booking payload submits the pickup in the airport's
      // timezone, not the booker's browser timezone.
      const pickupError = validatePickup(pickupFromFlight(landing, delayMin));
      if (pickupError) return { error: pickupError };
      // The one branch with a landing to report — and it is the same `landing` the committed value is
      // built from on the next line, so the triple reconciles by construction rather than by agreement.
      return {
        value: flightPickupCommitValue(landing, delayMin).toISOString(),
        delayMinutes: delayMin,
        // Built from the landing THIS lookup resolved, on the day `flightDate` asked about — so the day
        // looked up and the landing sent can never describe different flights.
        landingUsed: flightLandingUsedIso(landing),
      };
    }

    // An UNTOUCHED panel, not a mistyped one. The fields start empty, so this is now the
    // ordinary opening state and it needs its own message: "Enter a valid hour (1–12)" complains about a
    // field the client has not reached yet, which reads as the panel finding fault with their input.
    if (hour === "" && minute === "" && !meridiem) return { error: "Please select a time" };

    const h = parseInt(hour, 10);
    const rawM = minute === "" ? 0 : parseInt(minute, 10);
    if (isNaN(h) || h < 1 || h > 12) return { error: "Enter a valid hour (1–12)" };
    if (isNaN(rawM) || rawM < 0 || rawM > 59) return { error: "Enter a valid minute (0–59)" };
    // AM/PM is no longer pre-selected, so a bare "9" is genuinely ambiguous — and resolving it by
    // assuming one is precisely the twelve-hours-out booking this change exists to prevent. Asked for
    // last, so the client is told about a half-typed hour before being sent to the meridiem.
    if (!meridiem) return { error: "Select AM or PM" };

    // Snap onto the 5-min grid the backend expects.
    const m = Math.min(55, Math.round(rawM / STEP_MINUTES) * STEP_MINUTES);
    const hour24 = to24Hour(h, meridiem);
    // In flight mode the calendar holds the FLIGHT's date, which is not the pickup's whenever the booked
    // wait pushed the pickup past midnight (23:45 + 30 → 00:15 the next day). Manual entry renders over
    // precisely those bookings — tracking off, disrupted, lookup failed — so building the typed time on the
    // calendar's date would move the booking back onto the flight's day, a change the client never asked
    // for. An untouched calendar therefore keeps the pickup's own date; one the client actually moved is
    // what they chose, and wins.
    const pickup = new Date(!dateTouched && committedDate ? committedDate : date);
    pickup.setHours(hour24, m, 0, 0);

    // Validated as the instant that wall clock names AT THE PICKUP; committed as the wall clock itself.
    const pickupError = validatePickup(wallClockInstant(pickup, pickupZone));
    if (pickupError) return { error: pickupError };
    // A typed time is not derived from a flight, so it carries no wait, landing or flight date.
    return { value: pickup.toISOString(), delayMinutes: null, landingUsed: null };
  };

  /**
   * The pickup the DRIVER ARRIVES card states — `resolvePickup`'s own answer, not a second derivation.
   *
   * Read through the same call `handleProceed` commits from, so the time on screen and the time booked
   * cannot disagree. Recomputing `landing + delayMin` here would look identical and be wrong on the
   * anchored branch, which hands a booked pickup back untouched rather than rebuilding it — precisely
   * the booking where a reader is least able to spot a card that has invented its own number.
   *
   * Gated on `isFlightDerived` because that is the only branch that renders the card; `resolvePickup` is
   * pure (its docblock is explicit), so calling it for display costs some date math and no side effects.
   *
   * Null on an ERROR, which is the honest answer rather than a fallback: the two reachable errors here
   * are a pickup that has already elapsed and an absent date, and in both cases the panel is showing the
   * client why it cannot book that time. A card naming a time the Confirm would refuse would contradict
   * the message directly beneath it.
   */
  const flightPickupPreview = isFlightDerived ? resolvePickup() : null;
  const driverArrivesAt = flightPickupPreview && !("error" in flightPickupPreview) ? new Date(flightPickupPreview.value) : null;

  /**
   * The `arrival + delay = pickup` working under the pickup box — only where it adds up to the
   * pickup the box states (D11a). The re-derived branch adds up by construction: `resolvePickup` built the
   * pickup out of this landing and this wait. The ANCHORED branch hands a booked pickup back untouched, so
   * it gets the stored arrival only where `anchoredWorkingHolds` has checked the pair reconciles, and no
   * working otherwise.
   *
   * A stored arrival the flight has since moved off is labelled "Booked arrival": under the card's live
   * "Estimated arrival 10:25 PM", a second "Estimated arrival 9:25 PM" would be two answers to one question.
   */
  const workingArrival = !driverArrivesAt
    ? null
    : isAnchored
    ? frozenArrival && {
        iso: frozenArrival.iso,
        label:
          landing && formatFlightTime(landing) !== formatFlightTime(frozenArrival.iso)
            ? "Booked arrival"
            : legTimeLabel(frozenArrival.source, "arrival"),
      }
    : landing && { iso: landing, label: legTimeLabel(resolveLanding(flight)?.source, "arrival") };
  const pickupWorking: PickupWorking | null = workingArrival
    ? { arrivalLabel: workingArrival.label, arrivalIso: workingArrival.iso, delayMinutes: delayMin }
    : null;

  /**
   * What the flight card's footer discloses — every operand already computed above, handed over as one
   * struct so the footer cannot derive a time or a decision of its own.
   *
   * `pickupAt` is the BOOKED pickup and `proposedPickupAt` is where it lands on save, so the footer can
   * state the move as `9:25 PM → 9:18 PM` in one row. Both come from here rather than being diffed by the
   * reader across two boxes, which leaves the change itself unstated. On CREATE there is no booked pickup,
   * so the derived one is the pickup and the row states it once.
   *
   * `proposedPickupAt` is `driverArrivesAt`, the very value `PickupTimeCard` renders, so the arrow and
   * the card cannot disagree.
   *
   * `bookedGateIso` is withheld in two cases, both of them "this arrival is not that pickup's provenance":
   *
   *   - the client has SWAPPED flights, so `pickupFlightDerivedArrival` describes the one that was
   *     REPLACED, and labelling it "booked" beside the new flight's live arrival compares two different
   *     aircraft;
   *   - the client has changed the DATE, which is the same situation reached by another route. The lookup
   *     follows the calendar, so a new date resolves a DIFFERENT INSTANCE of the same flight number while
   *     the stored arrival still describes the old day's. On J000552 that printed EK414's 22 Sept gate
   *     against its 23 Sept gate as "+1469 min" of drift; nothing had drifted, the client picked another
   *     day. `DateTimePanel` already drops the stored DISRUPTION on the same flag and for the same reason
   *     (`flightDisruptionReason(dateTouched ? null : frozenReason)`); this guard simply never picked it up;
   *   - the stored arrival and the stored pickup do not reconcile. `anchoredWorkingHolds` above makes the
   *     same check for the same reason — `PickupTimeCalculator` clamps a pickup to `MIN_LEAD_MINUTES`, an
   *     operator can hand-edit one, and the wait can change after the last engine write — but it is built
   *     on `storedArrival`, which `rederive` nulls on any drift at all, so it reads false for exactly the
   *     bookings this disclosure exists for. This is that check taken against the BOOKED pair: booked
   *     arrival + booked wait, never the wait the client is currently typing, which is not part of what the
   *     booking says about itself.
   *
   * Either way the footer falls back to its single-gate-time variant, which is the honest shape — there is
   * one arrival in play and nothing to reconcile it against.
   *
   * `pickupMoves` is `rederive`, not `driftRederives`. The two differ on exactly the bookings that matter
   * here: `driftRederives` is computed purely from the lookup and so is true on a VIEW-ONLY booking whose
   * flight has moved, while `rederive` carries the `!viewOnly` gate that actually decides whether anything
   * is rebuilt. Wired to the former, the footer tells a reader of a closed booking that their pickup is
   * about to move, which is the one thing a closed booking guarantees it will not do.
   *
   * No threshold is passed at all. The 15/30 materiality rule is the ENGINE's and this path re-derives on
   * any drift (DEV-1531), so the footer has nothing to apply it to.
   */
  const storedPairReconciles = reconcilesWithBooked(storedLandingIso, committedDelay ?? 0);
  /**
   * WHY the pickup is not following the gate time, when the ENGINE has pinned it.
   *
   * Only the freeze reasons, which are the states the design covers. The other way a pickup holds — the
   * `rederived` guard declining a re-derivation `validatePickup` rejects — stays SILENT: the guard is
   * unchanged and still protects the booking, but naming it would mean copy the design does not have, and
   * the five-minute lead is a backend threshold nobody agreed to state to a client.
   */
  const holdReason: PickupHoldReason | null = isFlightPickupFrozen(frozenReason) ? (frozenReason as PickupHoldReason) : null;
  const disclosurePickupAt = committedDate ?? driverArrivesAt;
  // Withheld entirely on a CLOSED booking. `rederive` carries `!viewOnly`, so the pickup is pinned however
  // far the flight moves — and a block explaining a gap that will never be acted on only raises a question
  // whose answer is "no". The card's times row still shows what the flight did, so a reader loses no fact;
  // they are simply not told their pickup is about to follow it. Gated here rather than inside the card so
  // the dot and the disclosure narrow from the one source, and `useBookedFlightStatus` already skips
  // view-only for the summary badge — this is the same rule in the one place that knows.
  const pickupDisclosure: FlightPickupDisclosure | null =
    !viewOnly && flight && liveLanding && disclosurePickupAt
      ? {
          pickupAt: disclosurePickupAt,
          proposedPickupAt: driverArrivesAt,
          bookedGateIso: flightTouched || dateTouched || !storedPairReconciles ? null : storedLandingIso,
          liveGateIso: liveLanding,
          pickupMoves: rederive,
          holdReason,
          status: flight.status,
          delay: flight.delay,
        }
      : null;

  // Show any validation error without committing/advancing — used when a failed "Next Step" routes
  // the user here. With nothing pre-filled this now names what is missing — the date, or the time —
  // which is the reason the exit refused in the first place.
  const validate = () => {
    const result = resolvePickup();
    setError("error" in result ? result.error : null);
  };
  useForceValidate(forceValidate, validate);

  const handleProceed = () => {
    const result = resolvePickup();
    if ("error" in result) return setError(result.error);
    setError(null);

    // Snap the visible minute field to the committed grid value (manual path only) so the UI agrees.
    //
    // Never on the ANCHORED path, which committed a pickup it did not build out of these fields: an
    // off-grid booked minute (clamped, or hand-edited) would be snapped to a value the booking does not
    // hold, and `timeTouched` would then read that snap as an edit the client never made — releasing the
    // anchor on the next Confirm and re-validating an already-booked pickup, which is the elapsed-lead-time
    // block the branch exists to prevent.
    if (!isFlightDerived && !isAnchored) {
      const rawM = minute === "" ? 0 : parseInt(minute, 10);
      const m = Math.min(55, Math.round(rawM / STEP_MINUTES) * STEP_MINUTES);
      if (m !== rawM) setMinute(String(m).padStart(2, "0"));
    }

    // The three the server checks as one claim, committed together and only ever from the same branch —
    // the wait goes on the payload so the server can keep `pickup = flight arrival + wait` true as the
    // flight moves (DEV-1429), and the landing is what lets it verify the pair it was given rather than
    // resolving a second arrival of its own. An absent landing is always a safe answer; a wrong one is
    // refused, and on edit clears a previously-good applied arrival.
    dispatch(setPickupDatetime(result.value));
    dispatch(setPickupFlightDelayMinutes(result.delayMinutes));
    dispatch(setPickupFlightLandingUsed(result.landingUsed));
    // How this pickup time was arrived at, so a later lookup cannot re-apply a suggestion over a time
    // the client chose.
    //
    // Written whenever the DEPARTURE could own this time — a departure flight, and no arrival to
    // outrank it. Deliberately NOT gated on the suggestion having settled: a client who types a time
    // and presses Proceed while the lookup is still in flight has chosen it just as deliberately, and
    // recording nothing there let the next mount re-apply the server's suggestion over it (the local
    // hand-edit flag resets on mount, so this marker is the only thing that survives).
    //
    // Still skipped while an arrival owns the pickup: there is no suggestion to have accepted or
    // refused, and a "manual" recorded there would outlive the arrival — pressing "Not Flying"
    // afterwards flips the booking into the departure's case with its own suggestion already blocked.
    if (departureFlightNumber && !arrivalOwnsPickup) {
      dispatch(setPickupTimeSource(!timeHandEdited && appliedSuggestionKey.current ? "suggested" : "manual"));
    }
    if (layout === "sheet") onClose();
    else onAdvance?.();
  };

  /**
   * "Delay driver arrival by" — label, a full-width input with a "minutes" add-on, then the free-waiting
   * hint (RM-Client 7124-22723). `locked` shows the booked wait read-only, for a disrupted flight: the
   * pickup cannot be re-derived from a flight that is not operating, so the wait is not a lever there.
   */
  const delayField = (locked: boolean) => (
    <div className="flex flex-col gap-1">
      <label htmlFor="flight-delay-minutes">
        <Typography variant="action" className="block text-neutral-dark-gray">
          Delay driver arrival by
        </Typography>
      </label>
      <AddOnTextInput
        id="flight-delay-minutes"
        aria-label="Delay driver arrival by"
        inputMode="numeric"
        endAddOn="minutes"
        // AddOnTextInput wraps its input in a headlessui <Field> div that doesn't grow,
        // so w-full alone leaves the input content-width; grow the Field so it fills.
        className="w-full [&>div]:flex-1"
        disabled={locked || undefined}
        // The COMMITTED delay for a reader, never the input state. They agree today —
        // the state is seeded from `committedDelay` and a disabled input cannot move it —
        // but only one of them is the booking's own record, and a never-asked null must
        // not read as a deliberate 0 through a seeding default.
        value={viewOnly || locked ? String(committedDelay ?? 0) : delayMinutes}
        onChange={(e) => {
          clearError();
          setDelayMinutes(e.target.value.replace(/\D/g, "").slice(0, 3));
        }}
        onBlur={() => delayMinutes === "" && setDelayMinutes("0")}
      />
      {/* The operator's OWN numbers (Config → Trip → Waiting Times), not the platform
        defaults this sentence used to hardcode — an operator on 45/90 was telling every
        client 30/60 about how long their driver waits for free.

        Silent when the operator payload doesn't state them, which is the deliberate
        choice rather than a fallback: a default printed confidently is the bug being
        fixed here, and absence is transient (see `Operator.waitingTimes`). */}
      {waitingTimes && (
        <Typography variant="paragraph" className="block text-neutral-silver-gray">
          {`Free waiting time: ${waitingTimes.domesticAirport} min for domestic flights and ${waitingTimes.internationalAirport} min for international flights. It starts when the driver arrives.`}
        </Typography>
      )}
    </div>
  );

  /**
   * The pickup box for a disrupted flight (RM-Client 7130:26531): the BOOKED pickup with a red stroke, and
   * its working — off the SCHEDULED arrival as the design draws it, else off the arrival the booking was
   * actually derived from (an estimate that differed from the schedule). Only a working that adds up to the
   * booked pickup is shown (D11a).
   */
  const disruptedDelay = committedDelay ?? 0;
  const disruptedSchedule = scheduledLegTime(flight?.arrival);
  const disruptedWorking: PickupWorking | null = reconcilesWithBooked(disruptedSchedule, disruptedDelay)
    ? { arrivalLabel: legTimeLabel("scheduled", "arrival"), arrivalIso: disruptedSchedule!, delayMinutes: disruptedDelay }
    : reconcilesWithBooked(storedLandingIso, disruptedDelay)
    ? { arrivalLabel: "Booked arrival", arrivalIso: storedLandingIso!, delayMinutes: disruptedDelay }
    : null;

  /**
   * A cancelled or diverted flight is a DECISION point, not an input problem (RM-Client 7130:26176): the
   * card with both ends struck, the booked wait read-only, the booked pickup in a red box, and a banner
   * sending the client to the Address step to update the flight — or to cancel the trip.
   *
   * Rendered below the calendar, alongside the non-disrupted branch. The calendar is KEPT: rebooked onto the
   * same flight number a day later is a real journey, and re-dating is the only exit that serves it.
   *
   * The TIME FIELDS go because both real outcomes already have a home, and neither is a hand-typed time on
   * a booking still labelled with a dead flight: a new flight (the normal derived flow returns by itself —
   * the slice clears the stored reason on the number change), or not flying.
   *
   * NOT-FOUND and LOOKUP-ERROR keep their fields, below. Those are not disruptions; we simply cannot
   * confirm anything, and removing the fields there would block booking outright.
   *
   * DELIBERATELY NOT gated on the add-on, unlike the block below. The stored freeze reason is a fact the
   * booking already carries, so it reaches a closed booking and an operator who has turned tracking off
   * since — with tracking off the banner still lands and only the card is missing.
   *
   * View-only gets the card, the wait and the booked pickup, all inert (DEV-1510), and nothing to PRESS.
   */
  const disruptedFlight = (
    <div className="mt-6 flex flex-col gap-3">
      {/* `not_found` gets NO card. A cancelled flight is usually pulled from the schedule, so the lookup
          can come back empty while the booking still carries the disruption — and the card rendered off
          that is the flight number twice over an em-dash for each leg, which is worse evidence for the
          banner than no card at all. */}
      {flight && flight.status !== "not_found" && <FlightDetailsCard flight={flight} disruptionStatus={disruptedAs} />}
      {/* The booked wait and the booked pickup, as a pair — on create there is no booked pickup to describe. */}
      {committedDate && (
        <>
          {delayField(true)}
          <PickupTimeCard pickupAt={committedDate} working={disruptedWorking} tone="cancelled" />
        </>
      )}
      <Alert
        type="danger"
        className="border border-danger !px-2 !py-1"
        customIcon={<Icon name="InfoCircle" variant="Bold" size="md" className="shrink-0 self-start text-danger" />}
        message={
          <span className="flex flex-col items-start gap-1 text-left">
            <Typography variant="action" className="text-danger-dark">{`${pickupFlightNumber} has been ${disruptedAs}`}</Typography>
            {!viewOnly && (
              <>
                <Typography variant="caption" className="text-danger-dark">
                  Update the flight in the Address step to recalculate the pickup time, or cancel this trip.
                </Typography>
                {/* To the Address step, which owns the flight row, where the wizard provides one; else the
                    same flight modal that row opens — it handles both exits: a new number, or Not Flying. */}
                <button
                  type="button"
                  onClick={onUpdateFlight ?? (() => setFlightModalOpen(true))}
                  className="text-info hover:text-info-dark"
                >
                  <Typography variant="action">Update flight</Typography>
                </button>
              </>
            )}
          </span>
        }
      />
    </div>
  );

  /**
   * The hour/minute/AM-PM entry.
   *
   * ON A DEPARTURE BOOKING THESE STAY VISIBLE, deliberately, even though the SUGGESTED PICKUP has
   * already filled them in — so this is NOT the arrival's "date only" treatment and must not be made
   * to match it.
   *
   * The arrival can hide them because it leaves a lever behind: the client moves "delay driver
   * arrival by" and the pickup follows. A departure has no equivalent control, so the picker IS the
   * only way to move the pickup. Hiding it — behind a disclosure or otherwise — would leave the
   * suggestion with no route to change it, which is the trap this flow already hit on mobile.
   *
   * No branch is needed to achieve it: the flight block below is gated on `pickupFlightNumber`, the
   * ARRIVAL's, so a departure-only booking falls through to render this unconditionally. Pinned by
   * test, because a rule that costs no code is the kind someone later tidies away.
   */
  const manualTimeEntry = (
    <div className="mt-6">
      <Typography variant="action" className="block text-neutral-dark-gray">
        Enter time
      </Typography>
      {/* Uniform 9px between hour · colon · minute · AM/PM. */}
      <div className="mt-3 flex items-center gap-[9px]">
        {/* Placeholders earn their place now the fields open EMPTY — two identical blank
            boxes and a colon otherwise leave the reader to work out which half is which. */}
        <TimeField value={hour} onChange={editHour} onBlur={snapHour} max={12} placeholder="hh" ariaLabel="Hour" />
        <Typography variant="h2" className="text-neutral-dark-gray">
          :
        </Typography>
        <TimeField value={minute} onChange={editMinute} onBlur={snapMinute} max={59} placeholder="mm" ariaLabel="Minute" />
        {/* Stacked AM/PM — matches the fields' 80px height; radius only on the outer corners,
            a single mid-gray divider between the two.

            THE SELECTED HALF IS RINGED, not merely tinted. Two support tickets, one cause: clients could
            not tell which of the two they had chosen until the next step told them. It was measurable
            rather than a matter of taste — a `bg-primary-light` chip (#EBE7ED on the default theme)
            beside an unselected `bg-neutral-surface-gray` (#F9FAFB) is 1.17:1, so the two panels were
            near-identical. The text in each was perfectly legible, which is why nothing looked broken;
            there was simply no answer to "which one is on".

            THE RING IS THE SIGNAL, AND IT HAS TO BE, because `--color-primary` is per-operator branding
            (`useBranding`). Any cue carried by the FILL alone is only as strong as the tenant's palette —
            COD/Limo Australia brands black, and an operator with a pale primary would land right back on
            this defect with no code change. A 1px brand-coloured edge is legible at every brand: 7.38:1
            against the tint it frames on the default purple, 17.19:1 for COD/Limo's black.

            `ring` rather than `border`, so it draws over the 80px box instead of resizing it, and INSET
            so the selected half cannot overlap its sibling in the stack.

            This is the design system's own selected-segment treatment, not a new invention — tint +
            `ring-1 ring-primary` is exactly what `CheckboxButton` and `RadioButtonGroup` do, weight
            included. The meridiem pair had the tint and was missing the ring, which is the whole of the
            bug.

            A solid `bg-primary` fill was tried first and backed out: it reads well on the default plum
            and on COD/Limo's black, but `text-white` on a PALE branded primary is unreadable, and that
            failure mode has no test and no operator to catch it. Brand colour on brand tint relates
            correctly at every theme.

            VIEW-ONLY gets the same structure in a muted key. It was `bg-neutral-mid-gray` +
            `text-neutral-silver-gray` — 1.75:1, so a reader of a CLOSED booking could not tell whether
            it was booked for AM or PM either, which is the same defect on the surface that can do least
            about it: there is nothing to press to find out. Gray rather than brand, so it never invites
            a press the fieldset would refuse. */}
        <div className="flex h-20 flex-col">
          {MERIDIEMS.map((m, i) => (
            <button
              key={m}
              type="button"
              /* The same fact, for a screen reader: "which one is on" was unanswerable here too.
                 Matches the sibling LocationPanel's saved-toggle. */
              aria-pressed={meridiem === m}
              onClick={() => editMeridiem(m)}
              className={clsx(
                "flex-1 px-4 text-lg font-semibold",
                i === 0 ? "rounded-t-lg" : "rounded-b-lg border-t border-neutral-mid-gray",
                meridiem === m
                  ? viewOnly
                    ? "bg-neutral-mid-gray text-neutral-black ring-1 ring-inset ring-neutral-dark-gray"
                    : "bg-primary-light text-primary ring-1 ring-inset ring-primary"
                  : "bg-neutral-surface-gray text-neutral-dark-gray"
              )}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ViewOnlyFieldset>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
          <Typography variant="h2">Date and Time Pickup</Typography>
          <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
            Select a date and time for pickup
          </Typography>

          {/* Full-bleed: cancel the body's px-5 so the calendar spans the panel's inner width. View-only:
            day cells are <div>s (not form controls), so the fieldset can't disable them — block clicks
            with pointer-events-none and mute the selected day via the --view-only CSS. */}
          <div className={clsx("booking-calendar -mx-5 mt-6", viewOnly && "booking-calendar--view-only pointer-events-none")}>
            <DatePicker
              inline
              selected={date}
              onChange={(next) => {
                clearError();
                setDate(next);
              }}
              minDate={todayAtPickup(pickupZone)}
              calendarStartDay={1}
              formatWeekDay={(weekday) => weekday.slice(0, 2)}
              renderCustomHeader={({ monthDate, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    className="p-1 text-neutral-dark-gray disabled:text-neutral-silver-gray"
                  >
                    <Icon name="ArrowLeft2" size="md" />
                  </button>
                  <Typography variant="action" className="text-neutral-black">
                    {monthDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                  </Typography>
                  <button
                    type="button"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    className="p-1 text-neutral-dark-gray disabled:text-neutral-silver-gray"
                  >
                    <Icon name="ArrowRight2" size="md" />
                  </button>
                </div>
              )}
            />
          </div>

          {/* The calendar is the panel's primary control and stays first, so the order is the same in both
            states. See `disruptedFlight` for why the calendar survives a disruption at all. */}
          {isDisrupted && disruptedFlight}

          {/* SUGGESTED PICKUP (DEV-1487). Above the time fields, whichever branch renders them, because
            it explains the value sitting IN them.

            THE CARD ALWAYS, THE SUGGESTION SOMETIMES: a booking with a flight at each end shows both
            cards, because both are worth reading, but only one flight may propose a pickup time — see
            `arrivalOwnsPickup`.

            NOT FOR A READER. A closed booking's pickup is frozen while the flight keeps moving and the
            travel time is measured against today's traffic, so this would be a live-derived working
            beside a booked time it does not reconcile with — the thing the arrival card spent DEV-1510
            removing. */}
          {/* The drop-off lookup says so while it runs, exactly as the pickup's does (DEV-1619). Silent,
            the departure card arrives out of nowhere — or, on a flight that resolves to nothing, never
            arrives at all with nothing having said it was looking. */}
          {(isDepartureFetching || isFlightTrackingResolving) && departureFlightNumber && !viewOnly && (
            <Typography variant="muted" className="mt-6 block text-neutral-dark-gray">
              Finding flight {departureFlightNumber}…
            </Typography>
          )}
          {/* Said only once the lookup has actually SETTLED — the loading line above owns the window before
            that, and showing "we couldn't find it" over a request still in the air would be a lie that
            corrects itself. */}
          {!!departureFlightNumber &&
            flightTrackingEnabled &&
            !viewOnly &&
            !isDepartureFetching &&
            !isFlightTrackingResolving &&
            !departureResolved && <Alert className="mt-6" type={isDepartureError ? "warning" : "info"} message={departureNotice} />}
          {showDepartureCard && departureResolved && departureFlightNumber && (
            <div className="mt-6 flex flex-col gap-3">
              {/* The airline card FIRST, in departure mode, then the suggestion (DSN-611). The card is
                the evidence — which flight, which two airports, when it leaves and from which terminal —
                and the box below is what we propose doing about it. Reversing them puts a recommendation
                above the thing it is derived from. */}
              <FlightDetailsCard flight={departureResolved} leg="departure" />
              {departureSuggestionOffered && <SuggestedPickupCard flight={departureResolved} flightNumber={departureFlightNumber} />}
            </div>
          )}

          {/* A closed booking's booked TIME, disabled by the fieldset like every other view-only field.
            Working panels still get no time fields on a disruption — a reader is not choosing a pickup,
            so the reason those fields overlap the two real exits does not apply. */}
          {isDisrupted && viewOnly && !committedDate && manualTimeEntry}

          {!isDisrupted &&
            (pickupFlightNumber && flightTrackingEnabled ? (
              <div className="mt-6 flex flex-col gap-3">
                {isFlightFetching ? (
                  <Typography variant="muted" className="text-neutral-dark-gray">
                    Finding flight {pickupFlightNumber}…
                  </Typography>
                ) : flight && landing ? (
                  <>
                    <FlightDetailsCard flight={flight} pickupDisclosure={pickupDisclosure} />
                    {delayField(false)}
                    {/* The answer, directly under the two operands that produce it — landing on the card
                      above, minutes in the row above that. Null only where `resolvePickup` refuses, and
                      the refusal's own message renders below in its place. */}
                    {driverArrivesAt && <PickupTimeCard pickupAt={driverArrivesAt} working={pickupWorking} />}
                    {/* Only when the pickup box CANNOT show its working. `frozenArrival` is the arrival the
                      booked pickup was actually built from, so where it is set the box says the pickup out
                      loud: 5:10 AM + 50 min = the 6:00 AM booked. A time input beside that is a redundant
                      editable-looking control on a booking nobody can edit.

                      Where it is NULL the card has only the LIVE arrival, which the frozen pickup does not
                      reconcile against — a job closed before its flight landed is the ordinary way in, and
                      the flight goes on moving afterwards. Nothing on the card then adds up to the booked
                      pickup (measured: live 9:25 PM + 20 reads as 9:45 PM against a booked 6:00 PM), so the
                      booked time has to be on screen somewhere. `anchoredWorkingHolds` lists the other routes
                      here: a clamped pickup, an operator hand-edit, a delay changed after the last engine
                      write. */}
                    {viewOnly && !frozenArrival && manualTimeEntry}
                  </>
                ) : (
                  <>
                    {/* The client can always still book — manual time entry takes over. The reason differs:
                    lookup failed, the flight won't arrive as looked up, or it wasn't found. A cancelled or
                    diverted flight never reaches here; it is handled above, collapsed. */}
                    <Alert type={isFlightError ? "warning" : "info"} message={flightNotice} />
                    {manualTimeEntry}
                  </>
                )}
              </div>
            ) : (
              /* No flight number, or no flight-tracking add-on. View-only no longer lands here just for
               being view-only — it takes the branch above and gets the card — but a non-editable booking
               with tracking switched off still does, and shows the booked date and time from the hydrated
               draft with the wait published by the summary's own rows. */
              <>
                {manualTimeEntry}
                {pickupNotFlying && !departureFlightNumber && !viewOnly && (
                  <Alert
                    className="mt-6"
                    type="info"
                    message="You're not flying, so your driver arrives at the time you choose. Flight tracking doesn't apply."
                  />
                )}
              </>
            ))}
          {error && (
            <Typography variant="paragraph" className="mt-2 block text-danger">
              {error}
            </Typography>
          )}
        </div>
      </ViewOnlyFieldset>

      <FlightDetailsModal
        open={flightModalOpen}
        initialValue={pickupFlightNumber ?? ""}
        onConfirm={(flightNumber) => {
          confirmFlightNumber(flightNumber);
          setFlightModalOpen(false);
        }}
        onNotFlying={() => {
          notFlying();
          setFlightModalOpen(false);
        }}
      />

      {!viewOnly && (
        <div className="flex flex-col gap-2 px-5 pb-5">
          {/* While the flight is resolving, the manual fields are hidden, so a Proceed taken in that window
              decides the pickup off a lookup that has not answered yet — block it until the lookup settles.

              A DISRUPTION with nothing anchored is blocked permanently, and still is even though the
              seeded-time hazard it was written for is gone (`resolvePickup`'s manual branch used
              to read `defaultPickupSlot()` behind the hidden fields and commit the wall clock at mount).
              What is left is reason enough: this panel renders no time fields on a disruption, so the
              honest answer from that branch is now "Please select a time" — an instruction pointing at a
              control that is not on screen. Refusing the press says the same thing without the dead end.

              Anchored is exempt and must stay so: that branch has a committed pickup to hand back, so a
              client editing something else on a booking whose flight was cancelled is never trapped here.
              The unanchored case has its two exits in the modal above — a new flight, or Not Flying. */}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={handleProceed}
            /* The DEPARTURE lookup blocks Proceed for the same reason the arrival's does (DEV-1619): the
              suggested pickup is derived from it, so committing mid-round-trip commits a time the
              suggestion had no chance to produce — and the client is given no sign that anything was
              pending. Gated on there BEING a departure flight, so a booking without one is unaffected.
              `isFlightTrackingResolving` is in the same arm as on the arrival side, and for a sharper
              reason here: while the operator is unresolved the lookup SKIPS, so `isDepartureFetching` is
              false and nothing else would hold the button. Proceeding in that window commits the seeded
              manual time and stamps `pickupTimeSource: "manual"`, which permanently blocks the suggestion
              a moment later — the one failure on this path that cannot be undone by waiting. */
            disabled={
              (!!pickupFlightNumber && (isFlightFetching || isFlightTrackingResolving)) ||
              (!!departureFlightNumber && (isDepartureFetching || isFlightTrackingResolving)) ||
              (isDisrupted && !isAnchored)
            }
          >
            {editedDriverArrival ? `Confirm new time: ${format(editedDriverArrival, "h:mm a")}` : isEdit ? "Confirm" : "Proceed"}
          </Button>
          {layout === "sheet" && (
            <Button variant="custom" size="md" className="text-info" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
